import React, {useEffect, useMemo, useState, useContext} from 'react';

import {createLocalClient, createRemoteClient} from '@wandb/cg/browser';

import * as ServerApiTest from './cgservers/serverApiTest';
import * as ServerApiProd from './cgservers/serverApiProd';
import {useApolloClient} from './state/hooks';
import makeComp from './util/profiler';
import {backendWeaveUrl} from './config';
import {auth} from './setup';
import {viewerUsingAdminPrivileges} from './util/admin';
import {WeaveAppContext} from './cgreact.WeaveAppContext';

import ClientContext from './cgreact.ClientContext';

export const ComputeGraphContextProvider: React.FC<{test?: boolean}> = makeComp(
  ({test, children}) => {
    const {'weave-backend': useBackend} = useContext(WeaveAppContext);
    const apolloClient = useApolloClient();
    const cgClient = useMemo(
      () =>
        useBackend
          ? createRemoteClient(
              backendWeaveUrl(),
              auth.ensureCurrentIdToken.bind(auth),
              viewerUsingAdminPrivileges()
            )
          : createLocalClient(
              test
                ? new ServerApiTest.Client()
                : new ServerApiProd.Client(apolloClient)
            ),
      [useBackend, test, apolloClient]
    );
    const context = useMemo(() => ({client: cgClient}), [cgClient]);

    const [isLoading, setIsLoading] = useState(false);
    useEffect(() => {
      const subscription = context.client
        .loadingObservable()
        .subscribe(setIsLoading);
      return () => subscription.unsubscribe();
    }, [context]);

    return (
      <div
        data-test="compute-graph-provider"
        className={isLoading ? 'loading cg-executing' : ''}>
        <ClientContext.Provider value={context}>
          {children}
        </ClientContext.Provider>
      </div>
    );
  },
  {id: 'ComputeGraphContextProvider', memo: true}
);
